import React from "react";
import { DateTime, Duration } from "luxon";
import usePeriod from "@ui/Highcharts/hooks/usePeriod";
import { usePeriodComparison } from "./usePeriodComparison";
import { useReportingPeriodLimit } from "./useReportingPeriodLimit";
import { useReportingPeriod } from "./useReportingPeriod";
import type { Reporting, ReportingPeriod } from "../../../api/useReporting";

export function useReportingChartPeriod(
  reporting: Reporting,
  reportingPeriod: ReportingPeriod | null,
) {
  const limit = useReportingPeriodLimit(reporting, reportingPeriod);
  const [selectedPeriodComparison] = usePeriodComparison(reportingPeriod);
  const [selectedReportingChartPeriod] = useReportingPeriod({});

  const from = React.useMemo(
    () =>
      selectedPeriodComparison?.min
        ? DateTime.max(
            DateTime.fromISO(selectedPeriodComparison?.min).startOf("month"),
            DateTime.max(
              DateTime.fromMillis(limit.from),
              DateTime.fromMillis(
                limit.to -
                  (selectedReportingChartPeriod
                    ? Duration.fromISO(selectedReportingChartPeriod).toMillis()
                    : 0),
              ),
            ),
          ).toMillis()
        : limit.from,
    [limit.from, limit.to, selectedPeriodComparison?.min, selectedReportingChartPeriod],
  );

  const to = React.useMemo(
    () =>
      selectedPeriodComparison?.max
        ? DateTime.min(
            DateTime.fromISO(selectedPeriodComparison?.max).endOf("month"),
            DateTime.fromMillis(limit.to),
          ).toMillis()
        : limit.to,
    [limit.to, selectedPeriodComparison?.max],
  );

  const initialPeriod = React.useCallback(
    () => ({
      from,
      to,
    }),
    [from, to],
  );

  return usePeriod("chartReportingPeriod", limit, initialPeriod);
}
